import _ from "lodash";
import moment from "moment";
import {getNutsLevelTranslations} from "../../translations/getNutsLevelTranslations";
import {
  getCompleteValueDims,
  getCoordinatesArrayFromDataIdx,
  getDataIdxFromCoordinatesArray,
  getDimensionAttributeMap,
  getDimensionValueLabelFromCombinations,
  getDimensionValuesIndexesMap,
  getFormattedDimensionValueLabel,
  getObservationAttributeMap,
  VARIATION_DIMENSION_KEY,
  VARIATION_VALUE_VALUE_KEY
} from "../../utils/dataset";
import {MAP_CLASSIFICATION_METHOD_CUSTOM_INTERVALS} from "../../utils/map";
import {isNumeric} from "../../utils/validator";

export const getDetailLevelsFromGeometries = (geometries, showSingleGeometry, levelsOrder, t) =>
  geometries
    .reduce((acc, geom) => {
      const level = geom.level;
      const levelOrder = (levelsOrder || []).indexOf(level);
      if (!acc[level]) {
        acc[level] = {
          value: level,
          label: getNutsLevelTranslations(level, t),
          order: levelOrder > -1 ? levelOrder : Number.MAX_SAFE_INTEGER,
          counter: 0
        };
      }
      acc[level].counter++;
      return acc;
    }, [])
    .filter(val => showSingleGeometry || val.counter > 1)
    .sort((a, b) => a.order - b.order);

export const getLayoutGeometriesFromJsonStat = (
  geometries,
  jsonStat,
  layout,
  labelFormat,
  t,
  localizedTimePeriodFormatMapExternal = undefined,
  keepEmptyGeometries = false,
  dimensionValueTree = null,
  isPointData = false,
  latAttributeId = null,
  longAttributeId = null,
  allGeometriesMap = null
) => {
  const layoutGeometries = [];
  const geometryMap = {};
  const territoryMap = {};

  const geometryMapByTerritoryId = {};
  (geometries || []).forEach(g => (geometryMapByTerritoryId[g.id] = g));

  const datasetId = jsonStat?.extension?.datasets?.[0];

  const territoryDim = layout.territoryDim;
  const territoryDimValues = jsonStat.dimension[territoryDim].category.index;
  const indexesMap = getDimensionValuesIndexesMap(jsonStat);
  const obsAttributeMap = getObservationAttributeMap(jsonStat);
  const dimAttributeMap = getDimensionAttributeMap(jsonStat, t)[datasetId];

  let filtersValue = {...layout.filtersValue};
  if (jsonStat.id.includes(VARIATION_DIMENSION_KEY)) {
    filtersValue = {...filtersValue, [VARIATION_DIMENSION_KEY]: VARIATION_VALUE_VALUE_KEY};
  }

  const orderedDims = [
    layout.territoryDim,
    ...layout.filters,
    ...jsonStat.id.filter(dim => dim !== layout.territoryDim && !layout.filters.includes(dim))
  ];

  territoryDimValues.forEach(territoryDimValue => {
    const serverGeometry = geometryMapByTerritoryId[territoryDimValue];

    if (serverGeometry || keepEmptyGeometries) {
      let dimValueArray = jsonStat.id.map(dim => {
        if (dim === territoryDim) {
          return territoryDimValue;
        } else if (filtersValue[dim]) {
          return filtersValue[dim];
        } else {
          return null;
        }
      });

      if (dimValueArray.findIndex(val => val === null) > -1 && dimensionValueTree) {
        dimValueArray = getCompleteValueDims(dimValueArray, jsonStat.id, orderedDims, dimensionValueTree);
      }

      if (dimValueArray) {
        const dimValueIndexArray = dimValueArray.map((value, idx) => indexesMap[jsonStat.id[idx]][value]);
        const valueIdx = getDataIdxFromCoordinatesArray(dimValueIndexArray, jsonStat.size);

        const jsonStatValue = jsonStat.value[valueIdx];
        const value =
          jsonStatValue !== null && jsonStatValue !== "" && !isNaN(jsonStatValue) ? Number(jsonStatValue) : null;

        if (value !== null) {
          if (!serverGeometry) {
            if (allGeometriesMap !== null) {
              const identifier = allGeometriesMap[territoryDimValue];
              if (identifier) {
                layoutGeometries.push({
                  value: value,
                  id: territoryDimValue,
                  identifier: identifier,
                  geometry: {
                    type: "Polygon",
                    coordinates: []
                  },
                  label: getFormattedDimensionValueLabel(
                    jsonStat,
                    null,
                    territoryDim,
                    territoryDimValue,
                    labelFormat,
                    t,
                    localizedTimePeriodFormatMapExternal
                  )
                });
              }
            }
          } else {
            const obsAttributes = obsAttributeMap?.[valueIdx] || null;
            const dimAttributes = dimAttributeMap[territoryDim][territoryDimValue] || null;
            const hasAttributes = (obsAttributes || []).concat(dimAttributes || []).length > 0;

            let geometry;
            if (!isPointData) {
              geometry = serverGeometry.geoJson || serverGeometry.wkt;
            } else {
              const long = (dimAttributes || []).find(({id}) => id === longAttributeId)?.valueId;
              const lat = (dimAttributes || []).find(({id}) => id === latAttributeId)?.valueId;
              if (isNumeric(long) && isNumeric(lat)) {
                geometry = {
                  type: "Point",
                  coordinates: [Number(long), Number(lat)]
                };
              }
            }

            if (geometry) {
              layoutGeometries.push({
                value: value,
                id: serverGeometry.id,
                identifier: serverGeometry.uniqueId,
                level: serverGeometry.nutsLevel,
                geometry: geometry,
                label: getFormattedDimensionValueLabel(
                  jsonStat,
                  null,
                  territoryDim,
                  territoryDimValue,
                  labelFormat,
                  t,
                  localizedTimePeriodFormatMapExternal
                ),
                clickableTextPrefix: hasAttributes
                  ? t("components.map.tooltip.attributes.clickableTextPrefix")
                  : undefined,
                clickableText: hasAttributes ? t("components.map.tooltip.attributes.clickableText") : undefined,
                clickableTextSuffix: hasAttributes
                  ? t("components.map.tooltip.attributes.clickableTextSuffix")
                  : undefined,
                isTooltipClickable: hasAttributes
              });

              geometryMap[serverGeometry.uniqueId] = {
                hasAttributes: hasAttributes,
                obsAttributes: obsAttributes,
                dimAttributes: dimAttributes,
                territoryId: territoryDimValue
              };

              territoryMap[serverGeometry.id] = serverGeometry.uniqueId;
            }
          }
        }
      }
    }
  });

  return {
    layoutGeometries,
    geometryMap,
    territoryMap
  };
};

export const getLayoutGeometriesFromCombinations = (
  geometries,
  allGeometriesMap,
  dimensions,
  dimensionMap,
  territoryDim,
  territoryDimValues,
  data,
  seriesAttributeMap,
  observationAttributeMap,
  layout,
  t
) => {
  const layoutGeometries = [];
  const geometryMap = {};
  const territoryMap = {};

  const geometryMapByTerritoryId = {};
  (geometries || []).forEach(g => (geometryMapByTerritoryId[g.id] = g));

  territoryDimValues.forEach(territoryDimValue => {
    const serverGeometry = geometryMapByTerritoryId[territoryDimValue];

    if (serverGeometry) {
      const valueKey = dimensions
        .map(dim => (dim === territoryDim ? territoryDimValue : layout.filtersValue[dim]))
        .join("+");

      const dataValue = data?.values?.[valueKey]?.value ?? null;
      const value = dataValue !== null && dataValue !== "" && !isNaN(dataValue) ? Number(dataValue) : null;

      if (value !== null) {
        if (!serverGeometry) {
          if (allGeometriesMap !== null) {
            const identifier = allGeometriesMap[territoryDimValue];
            if (identifier) {
              layoutGeometries.push({
                value: value,
                id: territoryDimValue,
                identifier: identifier,
                geometry: {
                  type: "Polygon",
                  coordinates: []
                },
                label: getDimensionValueLabelFromCombinations(territoryDim, territoryDimValue, dimensionMap)
              });
            }
          }
        } else {
          if (serverGeometry.geoJson) {
            const obsAttributes = data?.values?.[valueKey]?.attributes
              ? data.values[valueKey].attributes.map(({id, value}) => ({
                  id: id,
                  label: observationAttributeMap[id].label,
                  valueId: value,
                  valueLabel: observationAttributeMap[id].values[value]
                }))
              : null;
            const dimAttributes = data?.dimensionsAttributes?.[territoryDim]?.[territoryDimValue]
              ? data.dimensionsAttributes[territoryDim][territoryDimValue].map(({id, value}) => ({
                  id: id,
                  label: seriesAttributeMap[id].label,
                  valueId: value,
                  valueLabel: seriesAttributeMap[id].values[value]
                }))
              : null;
            const hasAttributes = (obsAttributes || []).concat(dimAttributes || []).length > 0;

            layoutGeometries.push({
              value: value,
              id: serverGeometry.id,
              identifier: serverGeometry.uniqueId,
              level: serverGeometry.nutsLevel,
              geometry: serverGeometry.geoJson,
              label: getDimensionValueLabelFromCombinations(territoryDim, territoryDimValue, dimensionMap),
              clickableTextPrefix: hasAttributes
                ? t("components.map.tooltip.attributes.clickableTextPrefix")
                : undefined,
              clickableText: hasAttributes ? t("components.map.tooltip.attributes.clickableText") : undefined,
              clickableTextSuffix: hasAttributes
                ? t("components.map.tooltip.attributes.clickableTextSuffix")
                : undefined,
              isTooltipClickable: hasAttributes
            });

            geometryMap[serverGeometry.uniqueId] = {
              hasAttributes: hasAttributes,
              obsAttributes: obsAttributes,
              dimAttributes: dimAttributes,
              territoryId: territoryDimValue
            };

            territoryMap[serverGeometry.id] = serverGeometry.uniqueId;
          }
        }
      }
    }
  });

  return {
    layoutGeometries,
    geometryMap,
    territoryMap
  };
};

export const getTerritoriesWithValue = (jsonStat, territoryDim) => {
  const map = {};
  const territoryDimIdx = jsonStat.id.indexOf(territoryDim);

  Object.keys(jsonStat.value).forEach(key => {
    const val = jsonStat.value[key];
    if (val !== undefined && val !== null && val !== "") {
      const coordinates = getCoordinatesArrayFromDataIdx(key, jsonStat.size);
      const territoryDimValue = jsonStat.dimension[territoryDim].category.index[coordinates[territoryDimIdx]];
      map[territoryDimValue] = true;
    }
  });

  return Object.keys(map);
};

export const getTimeDimValueFromLayout = (timeDim, layout) => {
  const timeDimValue = timeDim && layout?.filtersValue?.[timeDim] ? layout.filtersValue[timeDim] : null;

  const timeDimValueYear = moment(timeDimValue);

  return timeDimValueYear.isValid() ? timeDimValueYear.format("YYYY") : null;
};

export const isSettingsChanged = (newSettings, prevSettings) => {
  let isChanged = false;

  if (!prevSettings) {
    return true;
  }

  for (const key in newSettings) {
    if (newSettings.hasOwnProperty(key)) {
      if (!_.isEqual(newSettings?.[key] || null, prevSettings?.[key] || null)) {
        isChanged = true;
      }
    }
  }

  return isChanged;
};

export const getCleanedSetting = settings => {
  const newSettings = {
    ..._.cloneDeep(settings),
    customIntervals:
      settings?.classificationMethod !== undefined
        ? settings.classificationMethod === MAP_CLASSIFICATION_METHOD_CUSTOM_INTERVALS
          ? settings.customIntervals
          : null
        : undefined
  };

  Object.keys(newSettings).forEach(key => {
    if (newSettings[key] === undefined || newSettings[key] === null) {
      delete newSettings[key];
    }
  });

  return newSettings;
};

export const getOverlayStyle = (theme, solidOverlay) => ({
  position: "absolute",
  zIndex: 3,
  color: "white",
  background: solidOverlay ? "#F5F5F5" : "rgba(0, 0, 0, 0.1)"
});

export const getOverlayTextStyle = (theme, solidOverlay) => ({
  fontWeight: solidOverlay ? "normal" : "bold",
  color: solidOverlay ? theme.palette.primary.main : "white"
});

export const getOverlayImageStyle = (theme, solidOverlay) => ({
  color: solidOverlay ? theme.palette.primary.main : "white"
});

export const getGeneralizationLevelFromZoomLevel = (zoomLevel, zoomLevelSteps, generalizationLevels) => {
  const idx = (zoomLevelSteps || []).findIndex(el => el >= zoomLevel);

  return idx === -1
    ? generalizationLevels[0]
    : idx === 0
      ? generalizationLevels[generalizationLevels.length - 1]
      : generalizationLevels[idx];
};
